import { DEFAULT_GROUP_ID } from "@/configs/runtime";
import { DEFAULT_HEADER_GROUP_ID } from "@/configs/system";
import { spaceApiAuthenticator } from "../clienteSpace";
import type {
	ISpace2FactorRequest,
	ISpaceGenerateQrCodeRequest,
	ISpaceOtpUserLookupRequest,
	ISpaceOtpUserLookupResponse,
} from "../dtos/2factor.dto";
import type { ISpaceLoginResponse } from "../dtos/authenticator.dto";

// --------------------------------- TYPES ---------------------------------
type VerifySpaceUsernameExistsOptions = {
	readonly contexto?: "sessao" | "login";
	readonly signal?: AbortSignal;
};

// -------------------------------- HELPERS --------------------------------
function normalizarGrupo(groupId?: string | null): string {
	return String(groupId || DEFAULT_GROUP_ID || "");
}

function normalizarNomeUsuario(nomeUsuario: string): string {
	return nomeUsuario.trim().toUpperCase();
}

function montarPayloadAutenticacaoOtp(payload: ISpace2FactorRequest, isSpaceUser: boolean): Record<string, unknown> {
	const body: Record<string, unknown> = {
		codigo_verificacao: payload.codigo_verificacao,
		id_cad_pessoa_funcionario: payload.id_cad_pessoa_funcionario,
		usuario: payload.usuario,
		platform: "web",
	};

	if (isSpaceUser && payload.nome_usuario?.trim()) body.nome_usuario = normalizarNomeUsuario(payload.nome_usuario);

	return body;
}

function montarPayloadQrCode(payload: ISpaceGenerateQrCodeRequest): Record<string, unknown> {
	const body: Record<string, unknown> = {
		nome_usuario: normalizarNomeUsuario(payload.nome_usuario),
	};

	if (payload.id_cad_grupo_bloqueado?.trim()) body.id_cad_grupo_bloqueado = payload.id_cad_grupo_bloqueado.trim();

	return body;
}

// ------------------------------------------------------------------- OTP
export async function twoFactorRequestOTP(payload: ISpace2FactorRequest, isSpaceUser = false) {
	return spaceApiAuthenticator
		.post("me/autenticacao-otp", {
			headers: { [DEFAULT_HEADER_GROUP_ID]: normalizarGrupo(payload.groupId) },
			json: montarPayloadAutenticacaoOtp(payload, isSpaceUser),
		})
		.json<ISpaceLoginResponse>();
}

export async function verifySpaceUsernameExists(
	payload: ISpaceOtpUserLookupRequest,
	{ contexto = "sessao", signal }: Readonly<VerifySpaceUsernameExistsOptions> = {},
) {
	const slug = payload.slug?.trim();
	if (contexto === "login" && !slug && !payload.groupId?.trim())
		throw new Error("Informe o grupo para validar o usuário SPACE.");

	const headers: Record<string, string> = {};
	if (!slug && payload.groupId) headers[DEFAULT_HEADER_GROUP_ID] = payload.groupId;

	const json = {
		...(payload.id === undefined
			? { nome_usuario: normalizarNomeUsuario(payload.nome_usuario) }
			: { id: payload.id }),
		...(slug ? { slug } : {}),
	};

	return spaceApiAuthenticator
		.post("me/procura-usuario-space-otp", {
			signal,
			...(contexto === "login" ? { hooks: { beforeRequest: undefined, afterResponse: undefined } } : {}),
			headers,
			json,
		})
		.json<ISpaceOtpUserLookupResponse>();
}

// ------------------------------------------------------------------- QR Code
export async function generateQrCodeOTP(payload: ISpaceGenerateQrCodeRequest) {
	return spaceApiAuthenticator
		.post("me/gerar-qrcode-otp", {
			headers: { [DEFAULT_HEADER_GROUP_ID]: normalizarGrupo(payload.groupId) },
			json: montarPayloadQrCode(payload),
		})
		.json<{ codigo_qr: string | { error: string } }>();
}

// ------------------------------------------------------------------- Desabilitar
export async function disableTwoFactorAuth(payload: { nome_usuario: string; groupId?: string }) {
	return spaceApiAuthenticator
		.post("me/desabilitar-otp", {
			headers: { [DEFAULT_HEADER_GROUP_ID]: normalizarGrupo(payload.groupId) },
			json: {
				nome_usuario: normalizarNomeUsuario(payload.nome_usuario),
			},
		})
		.json<{ codigo_qr: string | null }>();
}
